export const colors = {
  // Primary colors
  primary: {
    main: "#0EA5E9",
    light: "#38BDF8",
    dark: "#0284C7",
    hover: "#0EA5E9/90",
    shadow: "#0EA5E9/20",
  },
  
  // Background colors
  background: {
    main: "#212121",
    secondary: "#171717",
    card: "#1E1E1E",
    gradient: {
      from: "gray-800/50",
      to: "gray-800/30",
    },
  },
  
  // Text colors
  text: {
    primary: "white",
    secondary: "gray-300",
    muted: "gray-400",
    heading: "gray-100",
  },
  
  // Border colors
  border: {
    main: "gray-700/50",
    light: "white/10",
    hover: "gray-600/50",
  },

  // Landing page specific colors
  landing: {
    background: {
      main: "#0F172A", // Deeper blue background for landing
      secondary: "#1E293B", // Secondary background for sections
      card: "#334155", // Card background
    },
    text: {
      primary: "white",
      secondary: "#94A3B8",
      accent: "#38BDF8",
    },
    gradient: {
      primary: "bg-gradient-to-r from-blue-600 to-blue-500",
      secondary: "bg-gradient-to-br from-gray-800/80 to-gray-800/50",
      card: "bg-gradient-to-br from-gray-700/30 to-gray-800/30",
    }
  }
};

export const spacing = {
  card: {
    padding: {
      sm: "p-3",
      md: "p-4",
      lg: "p-6",
    },
    margin: {
      sm: "mb-4",
      md: "mb-6",
      lg: "mb-8",
    },
    gap: {
      sm: "gap-1.5",
      md: "gap-2",
      lg: "gap-4",
    },
  },
  
  text: {
    paragraph: "mb-4",
    heading: {
      sm: "mb-2",
      md: "mb-3",
      lg: "mb-4",
    },
  },
};

export const gradients = {
  card: "bg-gradient-to-r from-gray-800/50 to-gray-800/30",
  pill: "bg-gradient-to-br from-gray-700/80 to-gray-700/50",
  button: "bg-gradient-to-r from-blue-500 to-blue-600",
};

export const shadows = {
  sm: "shadow-sm",
  md: "shadow-md",
  lg: "shadow-lg",
};

export const borderRadius = {
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  full: "rounded-full",
};

export const typography = {
  size: {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl",
  },
  weight: {
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold",
    bold: "font-bold",
  },
};

// Commonly used component styles
export const componentStyles = {
  sectionHeading: {
    h2: "text-xl font-bold text-sky-400 leading-relaxed mb-4 mt-6",
    h3: "text-lg font-semibold text-sky-300 leading-relaxed mb-4 mt-5",
  },
  paragraph: "text-base text-gray-300 leading-relaxed mb-4",
  pill: "px-2 sm:px-3 py-0.5 sm:py-1 bg-gradient-to-br from-gray-700/80 to-gray-700/50 backdrop-blur-sm rounded-full text-xs sm:text-sm text-gray-200 border border-gray-600/20 shadow-sm hover:shadow-md transition-all",
  table: {
    container: "overflow-x-auto my-6 rounded-lg border border-gray-800",
    base: "min-w-full divide-y divide-gray-800",
    headerCell: "px-4 py-3 text-base text-gray-200 font-semibold",
    bodyCell: "px-4 py-3 text-base text-gray-300",
    headerRow: "bg-gray-800",
    bodyRow: "bg-gray-900/50",
    bodyRowAlt: "bg-gray-900/80",
  },
  landing: {
    section: "py-16 md:py-24",
    container: "container mx-auto px-4",
    heading: "text-3xl md:text-4xl font-bold text-white mb-6",
    subheading: "text-xl text-gray-300 mb-12 max-w-3xl mx-auto",
    hero: {
      wrapper: "relative overflow-hidden bg-landing-background-main min-h-screen flex items-center",
      content: "relative z-10 text-center max-w-4xl mx-auto px-4",
      title: "text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600",
      subtitle: "text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto",
      ctaWrapper: "flex flex-col sm:flex-row gap-4 justify-center mt-8",
      primaryButton: "px-8 py-3 rounded-lg font-medium bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 transform hover:scale-105 transition-all shadow-lg",
      secondaryButton: "px-8 py-3 rounded-lg font-medium bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transform hover:scale-105 transition-all",
    },
    value: {
      wrapper: "py-24 bg-landing-background-secondary",
      card: "p-6 backdrop-blur-sm rounded-xl bg-white/5 border border-white/10 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1",
      icon: "w-12 h-12 mb-4 text-blue-400",
      title: "text-xl font-semibold mb-2 text-blue-300",
      description: "text-gray-300",
    },
    howItWorks: {
      wrapper: "py-20 bg-gradient-to-b from-landing-background-main to-landing-background-secondary",
      step: "relative flex flex-col items-center text-center px-4",
      stepNumber: "w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xl mb-4",
      stepTitle: "text-xl font-semibold mb-2 text-white",
      stepDescription: "text-gray-300",
    },
    pricing: {
      wrapper: "py-24 bg-landing-background-secondary",
      planCard: "rounded-xl p-6 backdrop-blur-sm bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-white/10 shadow-xl h-full flex flex-col",
      popularBadge: "absolute -top-3 right-8 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium",
      planTitle: "text-xl font-bold mb-2 text-white",
      planPrice: "text-3xl font-bold mb-1 text-white",
      planPeriod: "text-sm text-gray-400 mb-4",
      planFeature: "flex items-center gap-2 mb-2 text-gray-300",
      planButton: "mt-auto rounded-lg px-4 py-2 font-medium bg-blue-500 text-white hover:bg-blue-400 transition-colors",
    }
  }
};

// Common layout patterns
export const layouts = {
  flexRow: "flex flex-row",
  flexCol: "flex flex-col",
  spaceBetween: "justify-between",
  spaceAround: "justify-around",
  itemsCenter: "items-center",
  itemsStart: "items-start",
  itemsEnd: "items-end",
  gap: {
    sm: "gap-2",
    md: "gap-4",
    lg: "gap-6",
  },
};
